import { useState } from "react";
import LegalDocument, { LegalList, LegalSection } from "../components/LegalDocument";

const storageItems = [
  ["techniahq_social_popup_seen", "Cookie", "Remembers that the social-follow prompt has already appeared.", "Up to 10 years unless removed."],
  ["techniahq_social_popup_closed", "Local storage", "Provides the same social-prompt memory when local storage is available.", "Until removed by the visitor or browser."],
  ["techniahq.ads.*", "Local or session storage", "Stores advertising state, frequency and consent information where the related feature is enabled.", "Varies by item and browser settings."],
];

export default function CookiePolicy() {
  const [status, setStatus] = useState("");

  const clearTechniaStorage = () => {
    if (typeof window === "undefined") return;
    const localPrefixes = ["techniahq"];
    [window.localStorage, window.sessionStorage].forEach((storage) => {
      try {
        Object.keys(storage).forEach((key) => {
          if (localPrefixes.some((prefix) => key.startsWith(prefix))) storage.removeItem(key);
        });
      } catch {
        // Browsers can block storage access. The visitor can still use browser settings.
      }
    });
    try {
      document.cookie = "techniahq_social_popup_seen=; Max-Age=0; Path=/; SameSite=Lax";
    } catch {
      // Browser controls remain available.
    }
    setStatus("TechniaHQRobot browser-storage markers were cleared on this device. Third-party cookies must be managed through the provider or browser settings.");
  };

  return (
    <LegalDocument
      path="/cookie-policy"
      title="Cookie Policy"
      seoTitle="Cookie Policy TechniaHQRobot"
      description="See which cookies and browser-storage items TechniaHQRobot uses for site functions, social prompts, advertising frequency and embedded content."
      eyebrow="Cookies and browser storage"
      updatedAt="August 4, 2026"
      schemaType="WebPage"
    >
      <LegalSection id="meaning" title="1. What cookies and browser storage are">
        <p>
          Cookies are small text records stored by a browser. Local storage and session storage serve similar purposes but are managed through browser APIs. They can remember a choice, reduce repeated prompts, protect a service, measure activity or support advertising.
        </p>
      </LegalSection>

      <LegalSection id="use" title="2. How TechniaHQRobot uses them">
        <LegalList>
          <li><strong>Functional storage</strong> remembers that the social-follow prompt has already appeared.</li>
          <li><strong>Advertising storage</strong> limits how often an ad format is requested and records which advertising zones loaded during a session.</li>
          <li><strong>Consent storage</strong> may preserve an advertising preference when a consent mechanism is active.</li>
          <li><strong>Third-party storage</strong> may be created by Google advertising, X embeds or other external media services.</li>
        </LegalList>
      </LegalSection>

      <LegalSection id="inventory" title="3. TechniaHQRobot storage inventory">
        <div className="overflow-x-auto rounded-2xl border border-neutral-200">
          <table className="min-w-[760px] w-full border-collapse text-left text-sm">
            <thead className="bg-neutral-950 text-white">
              <tr>
                <th className="px-4 py-3 font-black">Name</th>
                <th className="px-4 py-3 font-black">Type</th>
                <th className="px-4 py-3 font-black">Purpose</th>
                <th className="px-4 py-3 font-black">Typical duration</th>
              </tr>
            </thead>
            <tbody>
              {storageItems.map(([name, type, purpose, duration]) => (
                <tr key={name} className="border-t border-neutral-200 align-top even:bg-neutral-50">
                  <td className="px-4 py-4 font-mono text-xs font-bold text-neutral-950">{name}</td>
                  <td className="px-4 py-4 font-bold text-neutral-800">{type}</td>
                  <td className="px-4 py-4 leading-6 text-neutral-700">{purpose}</td>
                  <td className="px-4 py-4 leading-6 text-neutral-700">{duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          The inventory describes storage created by the current TechniaHQRobot source code. Advertising and embedded-content providers can use additional identifiers under their own policies, and those names can change without a TechniaHQRobot code release.
        </p>
      </LegalSection>

      <LegalSection id="third-party" title="4. Advertising and embedded content">
        <p>
          Google AdSense may use cookies, local storage, device identifiers or similar signals to deliver, limit, secure and measure advertising. X embeds may contact X when the widget loads and can use cookies or related technologies. Opening a social link transfers you to that platform.
        </p>
        <p>
          Where applicable law or a provider policy requires consent, the relevant consent mechanism should be used before non-essential storage or personalized advertising is activated. Google requires publishers serving personalized ads in the EEA, the United Kingdom or Switzerland to use a Google-certified consent-management platform.
        </p>
        <ul className="grid gap-3">
          <li><a className="font-black text-blue-700 underline underline-offset-4" href="https://support.google.com/adsense/answer/13554116?hl=en" target="_blank" rel="noopener noreferrer">Google consent-management requirements</a></li>
          <li><a className="font-black text-blue-700 underline underline-offset-4" href="https://help.x.com/en/rules-and-policies/x-cookies" target="_blank" rel="noopener noreferrer">How X uses cookies</a></li>
          <li><a className="font-black text-blue-700 underline underline-offset-4" href="https://commission.europa.eu/cookies-policy_en" target="_blank" rel="noopener noreferrer">European Commission cookie explanation</a></li>
        </ul>
      </LegalSection>

      <LegalSection id="controls" title="5. Your controls">
        <p>
          Browser settings can block or delete cookies, clear site data and restrict cross-site tracking. Blocking all storage can cause repeated prompts or prevent some embedded media and advertising functions from working as expected.
        </p>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
          <h3 className="text-lg font-black text-neutral-950">Clear TechniaHQRobot storage on this device</h3>
          <p className="mt-2 text-sm leading-7 text-neutral-700">
            This removes TechniaHQRobot frequency markers accessible to this page. It cannot remove HTTP-only records or every cookie created by a third-party domain.
          </p>
          <button
            type="button"
            onClick={clearTechniaStorage}
            className="mt-4 rounded-full bg-neutral-950 px-5 py-3 text-sm font-black text-white transition hover:bg-blue-700"
          >
            Clear site storage
          </button>
          {status ? <p className="mt-4 text-sm font-bold leading-6 text-blue-900" role="status">{status}</p> : null}
        </div>
      </LegalSection>

      <LegalSection id="updates" title="6. Updates">
        <p>
          This policy will be updated when storage names, advertising formats, embedded services or consent tools materially change. The latest revision date appears at the top of the page.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
